import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import confetti from "canvas-confetti";
import { useParams, useNavigate } from "react-router-dom";
import { useViewportHeight } from "./useViewportHeight";
import talkingPhaseLogo from "./talkingphase-logo-royal-purple-rgb-2000px-w-72ppi.png";
import bottomLogo from "./Logo-bottom.png";

// Configuration - Replace with your actual values
const CONFIG = {
  FORM_API_ENDPOINT:
    process.env.REACT_APP_FORM_API_ENDPOINT ||
    "https://your-api-gateway-url.amazonaws.com/prod/submit-form",
  TURNSTILE_SITE_KEY:
    process.env.REACT_APP_TURNSTILE_SITE_KEY || "your-turnstile-site-key",
};

function FormComponent() {
  const { goldenTicketId } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    orderNumber: "",
    secretCode: goldenTicketId || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileWidgetId, setTurnstileWidgetId] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const turnstileRef = useRef(null);

  // Handle mobile viewport height issues
  useViewportHeight();

  // Redirect to home if no golden ticket ID
  useEffect(() => {
    if (!goldenTicketId) {
      navigate("/");
      return;
    }
    // Initialize form with the golden ticket ID
    setFormData((prev) => ({
      ...prev,
      secretCode: goldenTicketId,
    }));
  }, [goldenTicketId, navigate]);

  const initializeTurnstile = () => {
    // Clear any existing Turnstile widget first
    if (window.turnstile && turnstileRef.current) {
      // Clear the container
      turnstileRef.current.innerHTML = "";

      // Reset widget ID if exists
      if (turnstileWidgetId !== null) {
        try {
          window.turnstile.remove(turnstileWidgetId);
        } catch (e) {
          // Ignore errors if widget doesn't exist
        }
        setTurnstileWidgetId(null);
      }

      // Only initialize if we don't have a valid token
      if (!turnstileToken) {
        const widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: CONFIG.TURNSTILE_SITE_KEY,
          callback: function (token) {
            setTurnstileToken(token);
            // Auto-submit immediately when Turnstile completes
            submitForm(token);
          },
          "error-callback": function () {
            setMessage({
              text: "Security verification failed. Please retry.",
              type: "error",
            });
            setIsSubmitting(false);
          },
        });
        setTurnstileWidgetId(widgetId);
      }
    }
  };

  const resetTurnstile = () => {
    if (window.turnstile && turnstileWidgetId !== null) {
      try {
        window.turnstile.reset(turnstileWidgetId);
      } catch (e) {
        // If reset fails, reinitialize
        initializeTurnstile();
      }
      setTurnstileToken("");
    }
  };

  const triggerSuccessConfetti = () => {
    const end = Date.now() + 3 * 1000; // 3 seconds
    const colors = ["#28a745", "#20c997", "#17a2b8", "#6f42c1"]; // Success color scheme

    const frame = () => {
      if (Date.now() > end) return;

      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        startVelocity: 60,
        origin: { x: 0, y: 0.5 },
        colors: colors,
      });
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        startVelocity: 60,
        origin: { x: 1, y: 0.5 },
        colors: colors,
      });

      requestAnimationFrame(frame);
    };

    frame();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.fullName.trim() || formData.fullName.trim().length < 2) {
      errors.push("Full name must be at least 2 characters long");
    }

    if (!formData.email.trim() || !formData.email.includes("@")) {
      errors.push("Please enter a valid email address");
    }

    if (
      !formData.orderNumber.trim() ||
      formData.orderNumber.trim().length < 3
    ) {
      errors.push("Order number must be at least 3 characters long");
    }

    if (!formData.secretCode.trim()) {
      errors.push("Secret code is required");
    }

    return errors;
  };

  const submitForm = async (token) => {
    // Validate form
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setMessage({
        text: validationErrors.join(", "),
        type: "error",
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await axios.post(
        CONFIG.FORM_API_ENDPOINT,
        {
          code: formData.secretCode.trim().toUpperCase(),
          fullName: formData.fullName.trim(),
          email: formData.email.trim(),
          orderNumber: formData.orderNumber.trim(),
          turnstileToken: token,
        },
        {
          timeout: 10000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setMessage({
          text: "🎉 Form submitted successfully! Thank you for your submission.",
          type: "success",
        });
        setIsSuccess(true);

        // Trigger success confetti
        triggerSuccessConfetti();
      } else {
        setMessage({
          text:
            response.data.message ||
            "Form submission failed. Please try again.",
          type: "error",
        });
        resetTurnstile();
      }
    } catch (error) {
      console.error("Form submission error:", error);
      let errorMessage = "An error occurred. Please try again.";

      if (error.code === "ECONNABORTED") {
        errorMessage = "Request timed out. Please try again.";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage({
        text: errorMessage,
        type: "error",
      });
      resetTurnstile();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setMessage({
        text: validationErrors.join(", "),
        type: "error",
      });
      return;
    }

    // Initialize Turnstile if not already done or if token is missing
    if (!turnstileToken) {
      setIsSubmitting(true);
      setMessage({ text: "", type: "" });
      initializeTurnstile();
      return;
    }

    // If we already have a token, submit directly
    setIsSubmitting(true);
    setMessage({ text: "", type: "" });
    await submitForm(turnstileToken);
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  return (
    <div className="golden-ticket-container">
      <div className="ticket-frame">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
        >
          {/* Horizontal borders (top and bottom) - normal thickness */}
          <path
            d="M94 1L6 1M6 99L94 99"
            stroke="black"
            strokeWidth="2.4"
            vectorEffect="non-scaling-stroke"
          />
          {/* Vertical borders (left and right) - thicker to compensate for distortion */}
          <path
            d="M0 6L0 94M100 6L100 94"
            stroke="black"
            strokeWidth="4.8"
            vectorEffect="non-scaling-stroke"
          />
          {/* Corner decorative curves */}
          <path
            d="M6 1C6 4 4 6 0 6M0 94C4 94 6 96 6 99M94 99C94 96 96 94 100 94M100 6C96 6 94 4 94 1"
            stroke="black"
            strokeWidth="3.0"
            vectorEffect="non-scaling-stroke"
            fill="none"
          />
        </svg>
      </div>
      <div className="ticket-content">
        <img
          src={talkingPhaseLogo}
          alt="Talking Phase"
          className="talking-phase-logo"
        />

        <div className="form-section">
          {!isSuccess ? (
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="fullName"
                className="secret-code-input"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="FULL NAME"
                disabled={isSubmitting}
                maxLength={100}
              />

              <input
                type="email"
                name="email"
                className="secret-code-input"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="EMAIL ADDRESS"
                disabled={isSubmitting}
                maxLength={100}
              />

              <input
                type="text"
                name="orderNumber"
                className="secret-code-input"
                value={formData.orderNumber}
                onChange={handleInputChange}
                placeholder="ORDER NUMBER"
                disabled={isSubmitting}
                maxLength={50}
              />

              <input
                type="text"
                name="secretCode"
                className="secret-code-input"
                value={formData.secretCode}
                onChange={handleInputChange}
                placeholder="SECRET CODE"
                disabled={true} // Always disabled as it comes from URL
                maxLength={50}
              />

              <button
                type="submit"
                className="claim-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="loading-spinner"></span>
                    SUBMITTING...
                  </>
                ) : (
                  <span className="claim-button-text">SUBMIT</span>
                )}
              </button>

              <div className="turnstile-container">
                <div ref={turnstileRef}></div>
              </div>

              {message.text && (
                <div className={`message ${message.type}`}>{message.text}</div>
              )}
            </form>
          ) : (
            <div className="success-content">
              <div className="message success">
                🎉 Thank you! Your form has been submitted successfully.
              </div>
              <button
                type="button"
                className="claim-button"
                onClick={handleBackToHome}
              >
                BACK TO HOME
              </button>
            </div>
          )}
        </div>

        <img src={bottomLogo} alt="Logo" className="bottom-logo" />
      </div>
    </div>
  );
}

export default FormComponent;
